import { useEffect, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DIFFS = ["easy", "normal", "hard"];
const DIFF_LABEL = { easy: "Easy", normal: "Normal", hard: "Hard" };

export default function ImportPanel({
  modelsCatalog,
  settings,
  onImport,
  onSaveDefaults,
}) {
  const [provider, setProvider] = useState("anthropic");
  const [model, setModel] = useState("");
  const [diff, setDiff] = useState("normal");
  const [count, setCount] = useState(50);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Initialise from stored defaults once settings arrive
  useEffect(() => {
    if (settings) {
      setProvider(settings.default_provider);
      setModel(settings.default_model);
    }
  }, [settings]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setRunning(true);
    setResult(null);
    try {
      const data = await onImport({
        difficulty: diff,
        count: Number(count),
        provider,
        model: model || undefined,
      });
      setResult(data);
      toast.success(`Added ${data.added} new phrases`, {
        description: `${data.skipped_duplicates} duplicates, ${data.skipped_invalid} invalid · via ${data.model_used}`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setRunning(false);
    }
  };

  const handleSaveDefault = async () => {
    try {
      await onSaveDefaults(provider, model);
      toast.success("Default model saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save defaults");
    }
  };

  const providerModels = modelsCatalog?.providers?.[provider] || [];

  return (
    <section className="bg-[#1A1A1A] text-[#F4F1EA] border-2 border-[#1A1A1A] shadow-[5px_5px_0px_#EB5E28] p-6">
      <div className="flex items-center gap-2 mb-1 text-[#EB5E28]">
        <Sparkles className="w-4 h-4" />
        <span className="font-sans-ui text-[10px] uppercase tracking-[0.24em] font-bold">
          {provider === "openai" ? "OpenAI" : "Anthropic"} · {model || "—"}
        </span>
      </div>
      <h2 className="font-serif-display text-2xl font-semibold mb-1">
        Commission a batch
      </h2>
      <p className="font-sans-ui text-sm text-[#F4F1EA]/70 mb-5">
        Up to 365 idioms per request. Duplicates and malformed entries are
        skipped automatically.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#EB5E28] mb-1.5">
            Provider
          </label>
          <div className="flex gap-2 flex-wrap">
            {[
              { id: "anthropic", label: "Anthropic" },
              { id: "openai", label: "OpenAI" },
            ].map((p) => {
              const has =
                (p.id === "openai" && settings?.has_openai_key) ||
                (p.id === "anthropic" && settings?.has_anthropic_key);
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => {
                    setProvider(p.id);
                    const first = modelsCatalog?.providers?.[p.id]?.[0]?.id;
                    if (first) setModel(first);
                  }}
                  data-testid={`admin-import-provider-${p.id}`}
                  className={`flex items-center gap-2 px-3 py-1.5 font-sans-ui text-xs uppercase tracking-[0.15em] font-semibold border-2 transition-all ${
                    provider === p.id
                      ? "bg-[#EB5E28] text-[#F4F1EA] border-[#EB5E28]"
                      : "bg-transparent text-[#F4F1EA] border-[#F4F1EA]/40 hover:border-[#F4F1EA]"
                  }`}
                >
                  {p.label}
                  <span
                    className={`text-[9px] tracking-[0.2em] ${
                      has ? "text-[#0F5132]" : "text-[#F4F1EA]/40"
                    }`}
                    title={has ? "Custom key" : "Uses Emergent fallback"}
                  >
                    {has ? "●" : "○"}
                  </span>
                </button>
              );
            })}
          </div>
          {settings && !settings.has_anthropic_key && !settings.has_openai_key && settings.emergent_fallback_available && (
            <p className="mt-2 font-sans-ui text-[10px] text-[#F4F1EA]/50 italic">
              Using Emergent universal key. Add your own in Settings to use a specific account.
            </p>
          )}
        </div>

        <div>
          <label className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#EB5E28] mb-1.5">
            Model
          </label>
          <div className="flex gap-2 items-center flex-wrap">
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              data-testid="admin-import-model"
              className="flex-1 min-w-[240px] bg-transparent border-2 border-[#F4F1EA]/40 px-3 py-2 font-sans-ui text-sm text-[#F4F1EA] focus:outline-none focus:border-[#EB5E28] cursor-pointer"
            >
              {providerModels.map((m) => (
                <option key={m.id} value={m.id} className="text-[#1A1A1A]">
                  {m.label}
                  {m.recommended ? " — recommended" : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleSaveDefault}
              data-testid="admin-save-default-model"
              className="px-3 py-2 font-sans-ui text-[10px] uppercase tracking-[0.15em] font-semibold border-2 border-[#F4F1EA]/40 hover:border-[#F4F1EA] transition-all"
            >
              Set as default
            </button>
          </div>
        </div>

        <div>
          <label className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#EB5E28] mb-1.5">
            Difficulty
          </label>
          <div className="flex gap-2 flex-wrap">
            {DIFFS.map((d) => (
              <button
                type="button"
                key={d}
                onClick={() => setDiff(d)}
                data-testid={`admin-import-diff-${d}`}
                className={`px-3 py-1.5 font-sans-ui text-xs uppercase tracking-[0.15em] font-semibold border-2 transition-all ${
                  diff === d
                    ? "bg-[#EB5E28] text-[#F4F1EA] border-[#EB5E28]"
                    : "bg-transparent text-[#F4F1EA] border-[#F4F1EA]/40 hover:border-[#F4F1EA]"
                }`}
              >
                {DIFF_LABEL[d]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#EB5E28] mb-1.5">
            Count (1–365)
          </label>
          <input
            type="number"
            min={1}
            max={365}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            data-testid="admin-import-count"
            className="w-32 bg-transparent border-2 border-[#F4F1EA]/40 px-3 py-2 font-serif-display text-xl tabular-nums focus:outline-none focus:border-[#EB5E28]"
          />
        </div>

        <button
          type="submit"
          disabled={running}
          data-testid="admin-import-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#EB5E28] text-[#F4F1EA] border-2 border-[#EB5E28] font-sans-ui font-bold uppercase tracking-[0.15em] text-xs hover:bg-[#F4F1EA] hover:text-[#1A1A1A] transition-all disabled:opacity-60"
        >
          {running ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Commissioning…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Import phrases
            </>
          )}
        </button>

        {result && (
          <div
            className="mt-3 border-t border-[#F4F1EA]/20 pt-3 font-sans-ui text-xs text-[#F4F1EA]/80 space-y-0.5"
            data-testid="admin-import-result"
          >
            <div>
              Requested <b>{result.requested}</b> · Added{" "}
              <b className="text-[#EB5E28]">{result.added}</b> · Duplicates{" "}
              {result.skipped_duplicates} · Invalid {result.skipped_invalid}
            </div>
            <div className="italic text-[#F4F1EA]/60">
              via {result.provider_used} / {result.model_used} (
              {result.key_source === "emergent" ? "Emergent fallback" : "custom key"})
            </div>
            {result.sample.length > 0 && (
              <div className="italic text-[#F4F1EA]/60 pt-1">
                Sample: {result.sample.slice(0, 3).join(" · ")}
              </div>
            )}
          </div>
        )}
      </form>
    </section>
  );
}
